(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('snf-shipping-carriers')?.textContent || '{"pairs":[]}');
  const pairs = new Map();
  for (const pair of config.pairs || []) {
    pairs.set(String(pair.wholesale), pair);
    if (pair.retail) pairs.set(String(pair.retail), pair);
  }
  const active = () => document.documentElement.classList.contains('snf-wholesale-active');
  const root = () => window.Shopify?.routes?.root || '/';
  const marker = '_snf_shipping_origin';
  let queue = Promise.resolve();
  function variantFor(id, wholesale = active()) {
    const pair = pairs.get(String(id));
    if (!pair || !pair.retail || !pair.available || pair.wholesalePrice !== pair.retailPrice) {
      throw new Error('This garment selection could not be verified. Please refresh the page or contact us.');
    }
    return wholesale ? pair.wholesale : pair.retail;
  }
  async function request(path, payload) {
    const response = await fetch(root() + path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(payload ? { body: JSON.stringify(payload) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Your garment cart could not be updated. Please try again.');
    return data;
  }
  const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const signature = (item) => String(item.variant_id) + ':' + propertiesKey(item.properties);
  function sourceToken(item) {
    let hash = 2166136261;
    for (const char of signature(item)) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
    return String(item.variant_id) + ':' + hash.toString(16);
  }
  async function synchronize(wholesale) {
    let cart = await request('cart.js');
    if (!Array.isArray(cart.items)) throw new Error('Your cart could not be checked. Please try again.');
    let changed = false;
    for (let iteration = 0; iteration < 250; iteration += 1) {
      const source = cart.items.find(item => pairs.has(String(item.variant_id)) && String(variantFor(item.variant_id, wholesale)) !== String(item.variant_id));
      if (!source) return { cart, changed };
      if (source.selling_plan_allocation) throw new Error('Please contact us to update this garment subscription.');
      const targetId = variantFor(source.variant_id, wholesale);
      const token = sourceToken(source);
      const sourceSignature = signature(source);
      const targetProperties = { ...(source.properties || {}), [marker]: token };
      const targetKey = propertiesKey(targetProperties);
      const findTarget = (state) => state.items.find(item => String(item.variant_id) === String(targetId) && propertiesKey(item.properties) === targetKey);
      let replacement = findTarget(cart);
      // Recover an interrupted add without adding the same garments twice.
      if (!replacement) {
        let addError;
        try {
          await request('cart/add.js', { items: [{ id: targetId, quantity: source.quantity, properties: targetProperties }] });
        } catch (error) { addError = error; }
        cart = await request('cart.js');
        replacement = findTarget(cart);
        if (!replacement) throw addError || new Error('Your garment selection was kept. Please try again.');
      }
      const currentSource = cart.items.find(item => signature(item) === sourceSignature);
      if (replacement.quantity !== source.quantity || (currentSource && currentSource.quantity !== source.quantity)) {
        throw new Error('Your cart changed while updating. Please review the garment quantities before continuing.');
      }
      if (currentSource) {
        let removeError;
        try { await request('cart/change.js', { id: currentSource.key, quantity: 0 }); }
        catch (error) { removeError = error; }
        cart = await request('cart.js');
        if (cart.items.some(item => signature(item) === sourceSignature)) {
          throw removeError || new Error('Please refresh your cart to finish updating the garment selection.');
        }
        if (!findTarget(cart)) throw new Error('Please review your garment selection before continuing.');
      }
      changed = true;
    }
    throw new Error('Please refresh your cart to finish checking the garment selections.');
  }
  function normalize(wholesale = active()) {
    const task = queue.then(() => synchronize(wholesale));
    queue = task.catch(() => undefined);
    return task;
  }
  window.SNFShippingCarriers = { variantFor, normalize };
  document.addEventListener('DOMContentLoaded', () => {
    if (!/\/cart\/?$/.test(window.location.pathname)) return;
    normalize().then(({ changed }) => {
      if (changed) window.location.reload();
    }).catch(error => window.alert(error.message));
  });
})();
